import { useMemo } from 'react';
import { useGroupQuery } from './useOSApi';
import { GROUP_NAME, MAIN_ITEMS } from '../config/constants';

// ============================== [類型定義] ==============================

interface CompanyInfo {
  name: string;
  logo: string;
}

interface Partner {
  id: number;
  category: string;
  logo: string;
  url: string;
}

interface Platform {
  id: string;
  url: string;
  type: string;
}

interface MainData {
  bannerImages: string[];
  mobileContentImages: string[];
  videoUrl: string;
  partners: Partner[];
}

interface FooterData {
  serviceDescription: string;
  platforms: Platform[];
}

interface UseAppDataReturn {
  companyInfo: CompanyInfo;
  mainData: MainData;
  footerData: FooterData;
  isLoading: boolean;
  error: string | null;
}

// ============================== [常量定義] ==============================
// 注意：現在使用統一配置文件中的常量

// ============================== [數據處理工具函數] ==============================

/**
 * 提取公司資訊
 */
const extractCompanyInfo = (groupDetail: any): CompanyInfo => {
  if (!groupDetail?.items?.length) {
    return { name: "", logo: "" };
  }

  const companyInfoItem = groupDetail.items
    .filter((item: any) => item.name === 'company_info')
    .sort((a: any, b: any) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];

  if (!companyInfoItem) {
    return { name: "", logo: "" };
  }

  const name = companyInfoItem.information?.companyName || "";
  const logo = companyInfoItem.blobs?.length > 0 ? companyInfoItem.blobs[0].url : "";

  return { name, logo };
};

/**
 * 提取主要內容數據
 */
const extractMainData = (groupDetail: any): MainData => {
  if (!groupDetail?.items?.length) {
    return {
      bannerImages: [],
      mobileContentImages: [],
      videoUrl: "",
      partners: []
    };
  }

  // 按類型分類並僅獲取最新的一個
  const itemsByType: Record<string, any> = {};
  groupDetail.items.forEach((item: any) => {
    if (!itemsByType[item.name] ||
      new Date(item.created_at) > new Date(itemsByType[item.name].created_at)) {
      itemsByType[item.name] = item;
    }
  });

  // 獲取橫幅圖片
  const bannerImages = itemsByType[MAIN_ITEMS.BANNER]?.blobs?.map(
    (blob: any) => blob.url
  ) || [];

  // 獲取移動內容圖片
  const mobileContentImages = itemsByType[MAIN_ITEMS.MOBILE]?.blobs?.map(
    (blob: any) => blob.url
  ) || [];

  // 獲取影片 URL
  const videoUrl = itemsByType[MAIN_ITEMS.VIDEO]?.information?.videoUrl || "";

  // 獲取合作夥伴
  const partners = itemsByType[MAIN_ITEMS.PARTNERS]?.information?.partners || [];

  return {
    bannerImages,
    mobileContentImages,
    videoUrl,
    partners
  };
};

/**
 * 提取頁腳數據
 */
const extractFooterData = (groupDetail: any): FooterData => {
  if (!groupDetail?.items?.length) {
    return {
      serviceDescription: "",
      platforms: []
    };
  }

  const footerInfoItem = groupDetail.items
    .filter((item: any) => item.name === 'footer_info')
    .sort((a: any, b: any) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];

  if (!footerInfoItem?.information) {
    return {
      serviceDescription: "",
      platforms: []
    };
  }

  const serviceDescription = footerInfoItem.information.serviceDescription || "";
  const platforms = footerInfoItem.information.platforms || [];

  return {
    serviceDescription,
    platforms
  };
};

// ============================== [主要 Hook] ==============================

/**
 * 應用數據管理 Hook
 * 封裝複雜的數據獲取和轉換邏輯
 */
export const useAppData = (): UseAppDataReturn => {
  // 1. 獲取群組列表
  const {
    data: groupsList,
    isLoading: isGroupsListLoading,
    error: groupsListError
  } = useGroupQuery({
    queryType: 'list'
  });

  // 2. 根據群組列表找到對應的群組 ID
  const groupIds = useMemo(() => {
    if (!groupsList?.data) return null;

    const headerGroup = groupsList.data.find((group: any) => group.name === GROUP_NAME.HEADER);
    const mainGroup = groupsList.data.find((group: any) => group.name === GROUP_NAME.MAIN);
    const footerGroup = groupsList.data.find((group: any) => group.name === GROUP_NAME.FOOTER);

    return {
      header: headerGroup?.id,
      main: mainGroup?.id,
      footer: footerGroup?.id
    };
  }, [groupsList]);

  // 3. 獲取各群組詳情
  const {
    data: headerDetail,
    isLoading: isHeaderLoading,
    error: headerError
  } = useGroupQuery({
    groupId: groupIds?.header || null,
    queryType: 'byId',
    enabled: !!groupIds?.header
  });

  const {
    data: mainDetail,
    isLoading: isMainLoading,
    error: mainError
  } = useGroupQuery({
    groupId: groupIds?.main || null,
    queryType: 'byId',
    enabled: !!groupIds?.main
  });

  const {
    data: footerDetail,
    isLoading: isFooterLoading,
    error: footerError
  } = useGroupQuery({
    groupId: groupIds?.footer || null,
    queryType: 'byId',
    enabled: !!groupIds?.footer
  });

  // 4. 數據轉換和計算
  const companyInfo = useMemo(() =>
    extractCompanyInfo(headerDetail?.data),
    [headerDetail]
  );

  const mainData = useMemo(() =>
    extractMainData(mainDetail?.data),
    [mainDetail]
  );

  const footerData = useMemo(() =>
    extractFooterData(footerDetail?.data),
    [footerDetail]
  );

  // 5. 載入狀態和錯誤處理
  const isLoading = isGroupsListLoading || isHeaderLoading || isMainLoading || isFooterLoading;

  const error = useMemo(() => {
    const errors = [groupsListError, headerError, mainError, footerError]
      .filter(Boolean)
      .map((err: any) => err?.message || '未知錯誤');

    return errors.length > 0 ? errors.join('; ') : null;
  }, [groupsListError, headerError, mainError, footerError]);

  return {
    companyInfo,
    mainData,
    footerData,
    isLoading,
    error
  };
}; 